import React, { FC, ReactElement, useRef, useState } from 'react';
import styled from 'styles/styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';

import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import { Navigation } from 'swiper/modules';
import media from 'utils/mediaStyle';
import {
  arrowNav,
  arrowRightButton,
  certifiedIngredients,
  clarityInvoice,
  clarityProcess,
  clearlyOriginal,
  convenienceUsp,
  onTime,
  quicklyDelivery,
  smartConvenience,
} from '../Images';
import { MainButton } from './CommonComponents';
import { SectionContainer, SectionTitle, SectionTitleBox } from './Partners';
import { FormattedMessage } from 'react-intl';
import messages from '../messages';
import ContactUsModal from './ContacUsModal';
import { EventName, sendGA4Event } from '../../../../utils/triggerGA4Event';
import { commonDimensions } from 'utils/constants';

const SwiperElementContainer = styled.div`
  max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px;
  margin: 0 auto;
  overflow: visible;
  position: relative;
  ${media.md`
    padding-left: 16px;
    `}
`;

const Container = styled.div`
  @media (max-width: 1023px) {
    padding: 0 16px;
  }
`;

const SwiperElement = styled(Swiper)`
  transform: translateX(-16px);
  // overflow: visible;
  margin: 0px !important;
  padding: 0px 16px !important;
  .swiper-button-prev.swiper-button-disabled {
    display: none !important;
  }
  .swiper-button-prev.swiper-button-disabled {
    display: none !important;
  }
  .swiper-button-next,
  .swiper-button-prev {
    display: none;
    &.swiper-button-disabled {
      display: none !important;
    }
  }
  ${media.md`
    padding: 10px 5px !important;
    `}
  @media(max-width: 1600px) {
    width: 100vw;
    max-width: unset;
  }
`;

const SwiperSlideOverlay = styled.div`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%
  background: #FFEEC3;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 32px 40px;
  gap: 12px;
  border-radius: 8px;
  transition: opacity 0.3s ease;
  ${({ greenBackground }) =>
    greenBackground &&
    `
          background: #E8F7C6
      `};
`;

const SwiperSlideElement = styled(SwiperSlide)`
  width: 100%; !important;
  position: relative;
  border-radius: 8px;
  &:last-child {
    margin-right: 200px;
  }
  @media (max-width: ${commonDimensions.MAX_WIDTH_CONTAINER}px) {
    &:last-child {
      margin-right: 50px;
    }
  }
  &:hover > ${SwiperSlideOverlay} {
    opacity: 1;
  }
  ${media.md`
    width: fit-content !important;
    `}

  @media(min-width: 1600px) {
    &:last-child {
      margin-right: 0px;
    }
  }
`;

const SwiperSlideImageWrapper = styled.div`
  position: relative;
  display: flex;
  overflow: hidden;
  ${({ image }) => image && `background: url(${image});`}
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  width: 100%;
  padding-top: 100%;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  ${media.md`
    width: 460px;
    border-radius: 8px;
    `}
`;

const SwiperSlideImageText = styled.div`
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  color: #ffffff;
  height: 70px;
  padding: 16px 20px;
  position: absolute;
  bottom: 0;
  display: flex;
  align-items: end;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 100%);
  width: 100%;
  font-size: 18px;
  ${media.md`
    font-size: 32px;
    line-height: 35px;
    font-weight: 600;
    height: 218px;
    padding: 32px;
    border-radius: 8px;
    `}
`;

const SwiperSlideTitleText = styled.div`
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  line-height: 40px;
  color: #343538;
`;

const SwiperSlideText = styled.div`
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  color: #343538;
`;

const SwiperSlideIcon = styled(SVGIcon)`
  margin-bottom: 20px;
`;

const SwiperSlideDescription = styled.div`
  background: #ffffff;
  padding: 16px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  height: 210px;
  ${media.md`
    height: 132px;
    `}
`;

const SwiperSlideDescriptionText = styled.span`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  display: -webkit-inline-box;
  -webkit-line-clamp: 9;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  overflow-wrap: anywhere;
  color: #383838;
`;

const NavigationButtonWrapper = styled.div`
  gap: 24px
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 40px;
  border-radius: 8px;
`;

const NavigationButton = styled.div`
  height: 56px;
  width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 80px;
  border: 2px solid #43a047;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    `
    border: 1px solid #b6e1b9;
    cursor: initial;
  `}
  &:hover {
    background: #f0f9f1 !important;
  }
  &:active {
    background: #d4edd5 !important;
  }
`;

const NavigationButtonIcon = styled(SVGIcon)`
  ${({ disabled }) => disabled && `filter: opacity(0.5);`}
`;

const Benefits: FC<{ customHeader?: ReactElement }> = ({ customHeader }) => {
  const lang = localStorage.getItem('lang') || 'vi';
  const swiperRef: any = useRef();
  const [isOpenContactModal, setIsOpenContactModal] = useState(false);
  const loggedIn = !!localStorage.getItem('token');

  const data = [
    {
      id: 1,
      img: clarityProcess,
      descriptionEn:
        'Right after payment, businesses receive electronic VAT invoices automatically via our system. No requests, no waiting, just clean, traceable documentation for hassle-free financial reporting.',
      description:
        'Ngay sau thanh toán, doanh nghiệp sẽ nhận hóa đơn VAT điện tử tự động qua hệ thống. Không cần yêu cầu riêng, không lo thất lạc, Kamereo giúp việc kê khai tài chính trở nên nhanh gọn và minh bạch tuyệt đối.',
      title: 'Hóa đơn VAT rõ ràng,{break}kê khai dễ dàng',
      titleEn: 'Clear invoices,{break}effortless reporting',
      descriptionIcon: clarityInvoice,
    },
    {
      id: 2,
      img: clearlyOriginal,
      descriptionEn:
        'Kamereo partners directly with VietGAP-certified (*) farms and implements a rigorous 4-layer quality control process from the farm, procurement center, and cold storage to final delivery. Every product is carefully inspected to ensure freshness, cleanliness, and consistency before reaching your kitchen.',
      description:
        'Kamereo hợp tác trực tiếp với các trang trại đạt chuẩn VietGAP và áp dụng quy trình kiểm soát chất lượng 4 lớp - từ nông trại, trung tâm thu mua, kho vận đến khâu giao hàng cuối cùng. Mỗi sản phẩm được kiểm tra kỹ lưỡng, đảm bảo chất lượng tươi mới, đạt chuẩn trước khi đến tay khách hàng.',
      title: 'Nguyên liệu đạt chuẩn,{break}bếp luôn sẵn sàng',
      titleEn: 'Certified ingredients,{break}kitchens always ready',
      descriptionIcon: certifiedIngredients,
    },
    {
      id: 3,
      img: convenienceUsp,
      descriptionEn:
        'Kamereo brings a tech-driven solution to streamline procurement for F&B businesses. With a curated catalog of 3,000+ products, you can place one order on one platform, without juggling multiple suppliers. Manage everything from order approvals and branch-level oversight to spend analytics in one place. Save time, stay in control, and run your operations more efficiently than ever.',
      description:
        'Kamereo mang đến giải pháp công nghệ với nền tảng mua hàng tối ưu quy trình thu mua trong ngành F&B. Với danh mục sản phẩm hơn 3.000 loại doanh nghiệp có thể đặt toàn bộ nhu cầu chỉ trong một đơn hàng, một nền tảng - không cần quản lý nhiều nhà cung cấp, không tốn thời gian. Bạn có thể quản lý toàn bộ quy trình từ phê duyệt đơn, quản lý chi nhánh đến phân tích thu chi, giúp tiết kiệm thời gian và vận hành hiệu quả.',
      title: 'Tiện lợi thông minh,{break}quản lý dễ dàng',
      titleEn: 'Smart convenience,{break}seamless management',
      descriptionIcon: smartConvenience,
    },
    {
      id: 4,
      img: quicklyDelivery,
      descriptionEn:
        'Kamereo accepts orders until midnight and guarantees delivery before 6 AM the next morning. With 99%+ on-time delivery, 98%+ full order completion, and 99.5%+ error-free products, your kitchen stays stocked and ready every day.',
      description:
        'Kamereo nhận đơn hàng đến 12h đêm và cam kết giao trước 6h sáng hôm sau. Với tỷ lệ 99%+ đơn giao đúng giờ, 98%+ giao đủ hàng và 99.5%+ không lỗi sản phẩm, Kamereo đảm bảo bếp luôn sẵn sàng khi bạn cần.',
      title: 'Đặt trễ vẫn kịp,{break}giao đúng - đủ - nhanh',
      titleEn: 'Always on time, fast,{break}and accurate, even on late orders',
      descriptionIcon: onTime,
    },
  ];
  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isNotMobile = width > 767;
        return (
          <SectionContainer green id="BENEFITS">
            <Container>
              {customHeader || (
                <SectionTitleBox>
                  {lang === 'vi' ? (
                    <>
                      <SectionTitle>Vì sao nên chọn&nbsp;</SectionTitle>
                      <SectionTitle highLight>mô hình “Từ nông trại đến doanh nghiệp“?</SectionTitle>
                    </>
                  ) : (
                    <>
                      <SectionTitle highLight>Why Choose Farm-Direct Sourcing&nbsp;</SectionTitle>
                      <SectionTitle>with Kamereo?</SectionTitle>
                    </>
                  )}
                </SectionTitleBox>
              )}
              <SwiperElementContainer>
                <SwiperElement
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                  slidesPerView={isNotMobile ? 'auto' : '1.3'}
                  spaceBetween={isNotMobile ? 24.5 : 16}
                  navigation={true}
                  modules={[Navigation]}
                  className="mySwiper"
                  // onReachEnd={() => {
                  //   setDisableNextBtn(true);
                  //   setDisablePreBtn(false);
                  // }}
                  // onReachBeginning={() => {
                  //   setDisablePreBtn(true);
                  //   setDisableNextBtn(false);
                  // }}
                  loop={true}
                >
                  {data.map((item, index) => {
                    const isGreenBackground = (index + 1) % 2 === 0;
                    return (
                      <SwiperSlideElement key={index}>
                        <SwiperSlideImageWrapper image={item.img}>
                          <SwiperSlideImageText
                            dangerouslySetInnerHTML={{
                              __html:
                                lang === 'vi'
                                  ? item.title.replace('{break}', '<br />')
                                  : item.titleEn.replace('{break}', '<br />'),
                            }}
                          />
                        </SwiperSlideImageWrapper>
                        {isNotMobile ? (
                          <SwiperSlideOverlay greenBackground={isGreenBackground}>
                            <SwiperSlideIcon src={item.descriptionIcon} />
                            <SwiperSlideTitleText
                              greenBackground={isGreenBackground}
                              dangerouslySetInnerHTML={{
                                __html:
                                  lang === 'vi'
                                    ? item.title.replace('{break}', '<br />')
                                    : item.titleEn.replace('{break}', '<br />'),
                              }}
                            />
                            <SwiperSlideText greenBackground={isGreenBackground}>
                              {lang === 'vi' ? item.description : item.descriptionEn}
                            </SwiperSlideText>
                          </SwiperSlideOverlay>
                        ) : (
                          <SwiperSlideDescription>
                            <SwiperSlideDescriptionText>
                              {lang === 'vi' ? item.description : item.descriptionEn}
                            </SwiperSlideDescriptionText>
                          </SwiperSlideDescription>
                        )}
                      </SwiperSlideElement>
                    );
                  })}
                </SwiperElement>
              </SwiperElementContainer>
              {isNotMobile && (
                <NavigationButtonWrapper>
                  <NavigationButton
                    onClick={() => {
                      swiperRef.current.slidePrev();
                    }}
                  >
                    <NavigationButtonIcon src={arrowNav} style={{ transform: 'rotate(180deg)' }} />
                  </NavigationButton>
                  <NavigationButton
                    onClick={() => {
                      swiperRef.current.slideNext();
                    }}
                  >
                    <NavigationButtonIcon src={arrowNav} />
                  </NavigationButton>
                </NavigationButtonWrapper>
              )}
              {!isNotMobile && !loggedIn && (
                <>
                  <div style={{ marginTop: '24px' }}>
                    <MainButton
                      fullWidth
                      onClick={() => {
                        sendGA4Event(EventName.PRODUCT_CONSULT_FTB_CLICK);
                        setIsOpenContactModal(true);
                      }}
                    >
                      <FormattedMessage {...messages.getProductConsultation} />
                      <SVGIcon src={arrowRightButton} />
                    </MainButton>
                  </div>
                  <ContactUsModal
                    isOpen={isOpenContactModal}
                    onCloseContactModal={() => setIsOpenContactModal(false)}
                  />
                </>
              )}
            </Container>
          </SectionContainer>
        );
      }}
    </Viewport.Consumer>
  );
};

export default Benefits;
